import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Plus, Edit, Trash2, Loader2, Search, Building, User, Mail, Phone, MapPin, ArrowUpDown, ArrowUp, ArrowDown, FileText, CheckCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

import { API } from '../utils/api';
import { openPrintWindow } from '../utils/openPrintWindow';

const Vendors = () => {
  const { getAuthHeaders, user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [formData, setFormData] = useState({
    name: '',
    vendor_type: 'vendor',
    email: '',
    phone: '',
    address: '',
    notes: '',
    default_category_id: '',
    tin: '',
    requires_1099: false,
    form_1099_type: 'NEC',
    city: '',
    state: '',
    zip_code: ''
  });

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const canEdit = user?.role === 'superadmin' || user?.role === 'manager';

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { headers: getAuthHeaders() });
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { headers: getAuthHeaders() });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    // Convert empty category to null for backend
    const submitData = {
      ...formData,
      default_category_id: formData.default_category_id || null
    };
    try {
      if (selectedVendor) {
        await axios.put(`${API}/vendors/${selectedVendor.id}`, submitData, { headers: getAuthHeaders() });
        toast.success('Vendor updated successfully');
      } else {
        await axios.post(`${API}/vendors`, submitData, { headers: getAuthHeaders() });
        toast.success('Vendor created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      name: vendor.name,
      vendor_type: vendor.vendor_type,
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      notes: vendor.notes || '',
      default_category_id: vendor.default_category_id || '',
      tin: vendor.tin || '',
      requires_1099: vendor.requires_1099 || false,
      form_1099_type: vendor.form_1099_type || 'NEC',
      city: vendor.city || '',
      state: vendor.state || '',
      zip_code: vendor.zip_code || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedVendor) return;
    try {
      await axios.delete(`${API}/vendors/${selectedVendor.id}`, { headers: getAuthHeaders() });
      toast.success('Vendor deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setSelectedVendor(null);
    setFormData({
      name: '',
      vendor_type: 'vendor',
      email: '',
      phone: '',
      address: '',
      notes: '',
      default_category_id: '',
      tin: '',
      requires_1099: false,
      form_1099_type: 'NEC',
      city: '',
      state: '',
      zip_code: ''
    });
  };

  const filteredVendors = vendors.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.email && v.email.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = typeFilter === 'all' || v.vendor_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedVendors = [...filteredVendors].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ column }) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
    }
    return <ArrowUpDown className="w-4 h-4 opacity-50" />;
  };

  const vendorCount = vendors.filter(v => v.vendor_type === 'vendor').length;
  const customerCount = vendors.filter(v => v.vendor_type === 'customer').length;

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const taxYears = [currentYear - 1, currentYear];

  const print1099 = async (vendorId, year) => {
    try {
      const [res, settingsRes] = await Promise.all([
        axios.get(`${API}/vendors/${vendorId}/1099/${year}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/company`, { headers: getAuthHeaders() })
      ]);
      const { vendor: v, forms } = res.data;
      const s = settingsRes.data;
      if (forms.length === 0) {
        toast.info(`No payments found for ${year}`);
        return;
      }
      const hasMasterAddr = s.master_company_name || s.master_address_line1;
      const masterAddr = hasMasterAddr ? [
        s.master_address_line1, s.master_address_line2,
        [s.master_city, s.master_state, s.master_zip].filter(Boolean).join(', ')
      ].filter(Boolean).join('<br>') : '';
      const masterPhone = s.master_phone || '';
      const masterEin = s.master_ein || '';
      const formType = v.form_1099_type || 'NEC';
      const typeLabels = {
        NEC: { name: '1099-NEC', desc: 'Nonemployee Compensation', box1: '1 Nonemployee compensation', box2: '2 Payer made direct sales totaling $5,000 or more', box3: '3', box4: '4 Federal income tax withheld' },
        MISC: { name: '1099-MISC', desc: 'Miscellaneous Information', box1: '1 Rents', box2: '2 Royalties', box3: '3 Other income', box4: '4 Federal income tax withheld' },
        INT: { name: '1099-INT', desc: 'Interest Income', box1: '1 Interest income', box2: '2 Early withdrawal penalty', box3: '3 Interest on U.S. Savings Bonds and Treasury obligations', box4: '4 Federal income tax withheld' },
        DIV: { name: '1099-DIV', desc: 'Dividends and Distributions', box1: '1a Total ordinary dividends', box2: '1b Qualified dividends', box3: '2a Total capital gain distributions', box4: '4 Federal income tax withheld' },
        R: { name: '1099-R', desc: 'Distributions From Pensions, Annuities, Retirement, etc.', box1: '1 Gross distribution', box2: '2a Taxable amount', box3: '3 Capital gain', box4: '4 Federal income tax withheld' },
        S: { name: '1099-S', desc: 'Proceeds From Real Estate Transactions', box1: '2 Gross proceeds', box2: '5 Buyer\'s part of real estate tax', box3: '6 Seller\'s share of real estate tax', box4: '4 Federal income tax withheld' },
        K: { name: '1099-K', desc: 'Payment Card and Third Party Network Transactions', box1: '1a Gross amount of payment card/third party network transactions', box2: '1b Card not present transactions', box3: '2 Merchant category code', box4: '4 Federal income tax withheld' },
        B: { name: '1099-B', desc: 'Proceeds From Broker and Barter Exchange Transactions', box1: '1d Proceeds', box2: '1e Cost or other basis', box3: '1f Accrued market discount', box4: '4 Federal income tax withheld' },
        G: { name: '1099-G', desc: 'Certain Government Payments', box1: '1 Unemployment compensation', box2: '2 State or local income tax refunds, credits, or offsets', box3: '3 Box 2 amount is for tax year', box4: '4 Federal income tax withheld' },
      };
      const labels = typeLabels[formType] || typeLabels.NEC;
      const fmtCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 2 }).format(amt || 0);
      const fmtEIN = (tin) => {
        if (!tin) return '';
        const d = tin.replace(/\D/g, '');
        if (d.length === 9) return d.slice(0,2) + '-' + d.slice(2);
        return tin;
      };
      const fmtSSN = (tin) => {
        if (!tin) return '';
        const d = tin.replace(/\D/g, '');
        if (d.length === 9) return d.slice(0,3) + '-' + d.slice(3,5) + '-' + d.slice(5);
        return tin;
      };
      const fmtPhone = (ph) => {
        if (!ph) return '';
        const d = ph.replace(/\D/g, '');
        if (d.length === 10) return '(' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6);
        return ph;
      };
      const copies = [
        { label: 'Copy B', sub: 'For Recipient' },
        { label: 'Copy C', sub: 'For Payer' }
      ];
      const buildForm = (f, copyLabel, copySub) => {
        const payerName = f.llc_name;
        const payerAddr = hasMasterAddr ? masterAddr : (f.llc_address || '');
        const payerPhone = hasMasterAddr ? masterPhone : (f.llc_phone || '');
        const payerTin = f.llc_ein;
        return `
        <div class="form-page">
          <div class="form-header">
            <div class="form-year">${year}</div>
            <div class="form-title">
              <div class="form-number">Form ${labels.name}</div>
              <div class="form-desc">${labels.desc}</div>
            </div>
            <div class="form-copy">${copyLabel}<br><span class="small">${copySub}</span></div>
          </div>
          <div class="form-body">
            <div class="form-row">
              <div class="form-field payer">
                <div class="field-label">PAYER'S name, street address, city or town, state or province, country, ZIP or foreign postal code, and telephone no.</div>
                <div class="field-value">
                  <strong>${payerName}</strong><br>
                  ${payerAddr}<br>
                  ${fmtPhone(payerPhone)}
                </div>
              </div>
              <div class="form-field-right">
                <div class="field-small">
                  <div class="field-label">PAYER'S TIN</div>
                  <div class="field-value tin">${fmtEIN(payerTin)}</div>
                </div>
                <div class="field-small">
                  <div class="field-label">RECIPIENT'S TIN</div>
                  <div class="field-value tin">${fmtSSN(f.vendor_tin)}</div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field recipient">
                <div class="field-label">RECIPIENT'S name</div>
                <div class="field-value"><strong>${f.vendor_name}</strong></div>
              </div>
              <div class="form-field-right">
                <div class="amount-box">
                  <div class="field-label">${labels.box1}</div>
                  <div class="amount-value">$ ${fmtCurrency(f.total_paid)}</div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field address">
                <div class="field-label">Street address (including apt. no.)</div>
                <div class="field-value">${f.vendor_address || ''}</div>
              </div>
              <div class="form-field-right">
                <div class="amount-box empty"><div class="field-label">${labels.box2}</div></div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field address">
                <div class="field-label">City or town, state or province, country, and ZIP or foreign postal code</div>
                <div class="field-value">${[f.vendor_city, f.vendor_state, f.vendor_zip].filter(Boolean).join(', ') || '&nbsp;'}</div>
              </div>
              <div class="form-field-right">
                <div class="amount-box empty"><div class="field-label">${labels.box3}</div></div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <div class="field-label">Account number (see instructions)</div>
                <div class="field-value">&nbsp;</div>
              </div>
              <div class="form-field-right">
                <div class="amount-box empty"><div class="field-label">${labels.box4}</div><div class="amount-value">$ 0.00</div></div>
              </div>
            </div>
          </div>
          <div class="form-footer">
            <div class="irs-ref">Form ${labels.name} (Rev. 1-${year})</div>
            <div class="dept-ref">Department of the Treasury - Internal Revenue Service</div>
          </div>
          <div class="txn-note">${f.transaction_count} transaction(s) totaling $${fmtCurrency(f.total_paid)} from ${f.llc_name}</div>
        </div>`;
      };
      const allPages = forms.flatMap(f =>
        copies.map(c => buildForm(f, c.label, c.sub))
      ).join('<div class="page-break"></div>');
      
      const html = `<!DOCTYPE html><html><head><title>${labels.name} - ${v.name} - ${year}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', monospace; font-size: 10pt; color: #000; padding: 0.5in; }
          .form-page { border: 2px solid #000; max-width: 7in; margin: 0 auto 0.5in; page-break-inside: avoid; }
          .form-header { display: flex; border-bottom: 2px solid #000; }
          .form-year { font-size: 24pt; font-weight: bold; padding: 8px 15px; border-right: 1px solid #000; display: flex; align-items: center; color: #c00; }
          .form-title { flex: 1; padding: 8px 12px; text-align: center; }
          .form-number { font-size: 14pt; font-weight: bold; }
          .form-desc { font-size: 9pt; margin-top: 2px; }
          .form-copy { padding: 8px 12px; border-left: 1px solid #000; text-align: center; font-size: 10pt; font-weight: bold; }
          .form-copy .small { font-size: 8pt; font-weight: normal; }
          .form-body { }
          .form-row { display: flex; border-bottom: 1px solid #000; min-height: 50px; }
          .form-row:last-child { border-bottom: none; }
          .form-field { flex: 1; padding: 4px 8px; border-right: 1px solid #000; }
          .form-field.payer { min-height: 80px; }
          .form-field-right { width: 220px; }
          .field-label { font-size: 7pt; color: #333; text-transform: uppercase; margin-bottom: 2px; }
          .field-value { font-size: 10pt; padding: 2px 0; }
          .field-value.tin { font-size: 12pt; font-weight: bold; letter-spacing: 1px; }
          .field-small { padding: 4px 8px; border-bottom: 1px solid #000; min-height: 35px; }
          .field-small:last-child { border-bottom: none; }
          .amount-box { padding: 4px 8px; min-height: 50px; }
          .amount-box.empty { }
          .amount-value { font-size: 14pt; font-weight: bold; text-align: right; padding-top: 4px; }
          .form-footer { display: flex; justify-content: space-between; padding: 4px 8px; border-top: 2px solid #000; font-size: 7pt; color: #666; }
          .txn-note { text-align: center; padding: 6px; font-size: 8pt; color: #666; font-style: italic; }
          .page-break { page-break-after: always; height: 0; }
          @page { margin: 0.4in; size: letter; }
          @media print { body { padding: 0; } .form-page { margin-bottom: 0.3in; } }
        </style></head><body>${allPages}</body></html>`;
      openPrintWindow(html);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load 1099 data');
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">
            Vendors & Customers
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your vendors and customers for transaction tracking
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-vendor-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor/Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {selectedVendor ? 'Edit' : 'New'} {formData.vendor_type === 'customer' ? 'Customer' : 'Vendor'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select
                        value={formData.vendor_type}
                        onValueChange={(value) => setFormData({ ...formData, vendor_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vendor">Vendor</SelectItem>
                          <SelectItem value="customer">Customer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Company or person name"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Street Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="City"
                        data-testid="vendor-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        placeholder="TX"
                        data-testid="vendor-state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ZIP</Label>
                      <Input
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="77001"
                        data-testid="vendor-zip-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Default Category</Label>
                    <Select
                      value={formData.default_category_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, default_category_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select default category (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="capitalize">{cat.category_type}</span>: {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Auto-fills category when selected in transactions</p>
                  </div>
                  <div className="space-y-2">
                    <Label>TIN (SSN or EIN)</Label>
                    <Input
                      value={formData.tin}
                      onChange={(e) => setFormData({ ...formData, tin: e.target.value })}
                      placeholder="XX-XXXXXXX or XXX-XX-XXXX"
                      data-testid="vendor-tin-input"
                    />
                    <p className="text-xs text-slate-500">Taxpayer Identification Number for 1099 reporting</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>1099 Required</Label>
                      <div className="flex items-center gap-3 pt-1">
                        <Switch
                          checked={formData.requires_1099}
                          onCheckedChange={(checked) => setFormData({ ...formData, requires_1099: checked })}
                          data-testid="vendor-requires-1099-toggle"
                        />
                        <span className="text-sm text-slate-600">{formData.requires_1099 ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                    {formData.requires_1099 && (
                      <div className="space-y-2">
                        <Label>1099 Type</Label>
                        <Select
                          value={formData.form_1099_type}
                          onValueChange={(value) => setFormData({ ...formData, form_1099_type: value })}
                        >
                          <SelectTrigger data-testid="vendor-1099-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NEC">1099-NEC (Nonemployee Compensation)</SelectItem>
                            <SelectItem value="MISC">1099-MISC (Miscellaneous)</SelectItem>
                            <SelectItem value="INT">1099-INT (Interest Income)</SelectItem>
                            <SelectItem value="DIV">1099-DIV (Dividends)</SelectItem>
                            <SelectItem value="R">1099-R (Retirement Distributions)</SelectItem>
                            <SelectItem value="S">1099-S (Real Estate Proceeds)</SelectItem>
                            <SelectItem value="K">1099-K (Payment Card / 3rd Party)</SelectItem>
                            <SelectItem value="B">1099-B (Broker Transactions)</SelectItem>
                            <SelectItem value="G">1099-G (Government Payments)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={formLoading}>
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedVendor ? 'Update' : 'Create')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-serif font-semibold">{vendors.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Vendors</p>
                <p className="text-2xl font-serif font-semibold">{vendorCount}</p>
              </div>
              <Building className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Customers</p>
                <p className="text-2xl font-serif font-semibold">{customerCount}</p>
              </div>
              <User className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="vendor">Vendors</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-serif">Directory</CardTitle>
          <CardDescription>{filteredVendors.length} entries</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredVendors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-serif text-lg font-semibold text-slate-700">No vendors or customers</h3>
              <p className="text-slate-500 mt-1">
                {searchQuery || typeFilter !== 'all' ? 'Try different filters' : 'Add your first vendor or customer'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer hover:bg-slate-700" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-2">Name <SortIcon column="name" /></div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-700" onClick={() => handleSort('vendor_type')}>
                      <div className="flex items-center gap-2">Type <SortIcon column="vendor_type" /></div>
                    </TableHead>
                    <TableHead>Default Category</TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-700" onClick={() => handleSort('email')}>
                      <div className="flex items-center gap-2">Email <SortIcon column="email" /></div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-700" onClick={() => handleSort('phone')}>
                      <div className="flex items-center gap-2">Phone <SortIcon column="phone" /></div>
                    </TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>1099 Forms</TableHead>
                    {canEdit && <TableHead className="w-[80px]"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedVendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell className="font-medium">{vendor.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={vendor.vendor_type === 'customer' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                          {vendor.vendor_type === 'customer' ? <User className="w-3 h-3 mr-1" /> : <Building className="w-3 h-3 mr-1" />}
                          {vendor.vendor_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getCategoryName(vendor.default_category_id) ? (
                          <span className="text-sm text-slate-600">{getCategoryName(vendor.default_category_id)}</span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {vendor.email ? (
                          <span className="flex items-center gap-1 text-sm">
                            <Mail className="w-3 h-3 text-slate-400" />
                            {vendor.email}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {vendor.phone ? (
                          <span className="flex items-center gap-1 text-sm">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {vendor.phone}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {vendor.address ? (
                          <span className="flex items-center gap-1 text-sm text-slate-600 max-w-[200px] truncate">
                            <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            {vendor.address}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {vendor.requires_1099 ? (
                          <div className="flex gap-1 items-center">
                            <Badge variant="outline" className="text-xs mr-1 bg-amber-50 text-amber-700 border-amber-200">
                              {vendor.form_1099_type || 'NEC'}
                            </Badge>
                            {taxYears.map(yr => (
                              <Button
                                key={yr}
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                data-testid={`vendor-1099-${vendor.id}-${yr}`}
                                onClick={() => print1099(vendor.id, yr)}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                {yr}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(vendor)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700"
                              onClick={() => { setSelectedVendor(vendor); setDeleteDialogOpen(true); }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedVendor?.vendor_type}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedVendor?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedVendor(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Vendors;
